// CartHub Dashboard — Subscription Component.
// Piani Stripe, checkout, portale fatturazione.

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api, Ic } from "../shared";
import { dateLocaleForAppLang } from "../i18n";

/** Voce piano da GET /api/stripe-merchant/plans: oggetto `{ id, limit? }` o stringa legacy. */
function subscriptionPlanFeatureLine(feature, t) {
  if (feature == null) return "";
  if (typeof feature === "string") return feature;
  const id = feature.id;
  if (!id) return "";
  if (id === "carts_monthly" && feature.limit != null) {
    return t(`features.${id}`, { limit: feature.limit });
  }
  return t(`features.${id}`);
}

export default function Subscription({ shop, onSubscriptionSynced }) {
  const { t, i18n } = useTranslation("subscription");
  const dateLoc = dateLocaleForAppLang(i18n.language);
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(dateLoc, { day: "2-digit", month: "long", year: "numeric" }) : "—");

  const [plans, setPlans] = useState([]);
  const [interval, setIntervalType] = useState("yearly");
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    fetch("https://app.carthub.cloud/api/stripe-merchant/plans")
      .then((r) => r.json())
      .then((d) => {
        setPlans(d.plans || []);
        setLoading(false);
      });
  }, []);

  // Dopo portale Stripe o navigazione su Abbonamenti: allinea commercial_state / integration_active (banner).
  useEffect(() => {
    if (!onSubscriptionSynced || shop?.is_agency_managed) return;
    let cancelled = false;
    (async () => {
      const d = await api("/stripe-merchant/subscription");
      if (!cancelled && d && !d.error) onSubscriptionSynced(d);
    })();
    return () => {
      cancelled = true;
    };
  }, [onSubscriptionSynced, shop?.is_agency_managed]);

  const startCheckout = async (priceId) => {
    setCheckoutLoading(priceId);
    setMsg("");
    const d = await api("/stripe-merchant/checkout", { method: "POST", body: JSON.stringify({ price_id: priceId }) });
    if (d.checkout_url) {
      window.location.href = d.checkout_url;
    } else {
      setMsg(d.error || t("errorCheckout"));
      setCheckoutLoading(null);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    const d = await api("/stripe-merchant/portal", { method: "POST" });
    if (d.portal_url) window.location.href = d.portal_url;
    else {
      setMsg(d.error || t("errorPortal"));
      setPortalLoading(false);
    }
  };

  const currentPlan = shop?.plan || "trial";
  const isActive = currentPlan && !["trial", "cancelled"].includes(currentPlan);

  return (
    <div>
      {new URLSearchParams(window.location.search).get("checkout") === "success" && (
        <div className="ch-info" style={{ background: "#f0faf0", border: "1px solid #c8e6c9", color: "#2e7d32", marginBottom: 20 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span style={{ display: "inline-flex", color: "#2e7d32" }}>{Ic.check}</span>
            <strong>{t("checkoutBold")}</strong>
            {t("checkoutRest")}
          </span>
        </div>
      )}
      {msg && <div className="ch-info" style={{ background: "#fff7ed", border: "1px solid #fed7aa", color: "#c2410c", marginBottom: 16 }}>{msg}</div>}

      {isActive && (
        <div className="ch-card" style={{ marginBottom: 20, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
          <div className="card-head">
            <h2>{t("current.title")}</h2>
            <span className="ch-status success">
              <span className="status-dot" />
              {t("current.badgeActive")}
            </span>
          </div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>{t("current.plan")}</div>
              <div style={{ fontWeight: 600, textTransform: "capitalize" }}>{currentPlan}</div>
            </div>
            <div>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>{t("current.billing")}</div>
              <div style={{ fontWeight: 600 }}>{shop?.billing_interval === "yearly" ? t("current.yearly") : t("current.monthly")}</div>
            </div>
            <div>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>{t("current.cartsPerMonth")}</div>
              <div style={{ fontWeight: 600 }}>{shop?.carts_limit || "—"}</div>
            </div>
            {shop?.current_period_end && (
              <div>
                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>{t("current.nextRenewal")}</div>
                <div style={{ fontWeight: 600 }}>{fmtDate(shop.current_period_end)}</div>
              </div>
            )}
          </div>
          <button type="button" className="ch-btn" style={{ display: "inline-flex", alignItems: "center", gap: 8 }} onClick={openPortal} disabled={portalLoading}>
            {portalLoading ? (
              t("portalOpening")
            ) : (
              <>
                <span style={{ display: "inline-flex" }}>{Ic.settings}</span>
                {t("managePortal")}
              </>
            )}
          </button>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 28 }}>
        <span style={{ fontSize: 15, fontWeight: 500, color: interval === "monthly" ? "#1a1a1a" : "#9ca3af" }}>{t("toggleMonthly")}</span>
        <div
          onClick={() => setIntervalType(interval === "monthly" ? "yearly" : "monthly")}
          style={{
            width: 44,
            height: 24,
            borderRadius: 12,
            background: interval === "yearly" ? "#0A85CF" : "#e5e5e5",
            cursor: "pointer",
            position: "relative",
            transition: "background .2s",
          }}
        >
          <div
            style={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              background: "#fff",
              position: "absolute",
              top: 3,
              left: interval === "yearly" ? 23 : 3,
              transition: "left .2s",
              boxShadow: "var(--shadow-module)",
            }}
          />
        </div>
        <span style={{ fontSize: 15, fontWeight: 500, color: interval === "yearly" ? "#1a1a1a" : "#9ca3af" }}>
          {t("toggleYearly")}{" "}
          <span style={{ fontSize: 13, color: "#0A85CF", fontWeight: 600 }}>{t("saveUpTo")}</span>
        </span>
      </div>

      {loading ? (
        <div className="empty">
          <div className="empty-icon-svg" aria-hidden>
            {Ic.loader}
          </div>
          <div className="empty-title">{t("loadingPlans")}</div>
        </div>
      ) : (
        <div className="subscription-plans-grid">
          {plans.map((plan) => {
            const priceId = interval === "yearly" ? plan.price_id_yearly : plan.price_id_monthly;
            const price = interval === "yearly" ? plan.price_yearly : plan.price_monthly;
            const isCurrent = currentPlan === plan.key;
            return (
              <div
                key={plan.key}
                style={{
                  background: "var(--surface-bright)",
                  border: `2px solid ${plan.popular ? "#0A85CF" : "#e5e5e5"}`,
                  borderRadius: "var(--radius-module)",
                  padding: 24,
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                }}
              >
                {plan.popular && (
                  <div
                    style={{
                      position: "absolute",
                      top: -12,
                      left: "50%",
                      transform: "translateX(-50%)",
                      background: "#0A85CF",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 600,
                      padding: "3px 12px",
                      borderRadius: 20,
                      whiteSpace: "nowrap",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span style={{ display: "inline-flex" }}>{Ic.star}</span>
                    {t("badgePopular")}
                  </div>
                )}
                <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>{plan.name}</div>
                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>{t("cartsPerMonthLabel", { count: plan.carts_limit })}</div>
                <div style={{ marginBottom: 4 }}>
                  <span style={{ fontSize: 32, fontWeight: 700 }}>€{price}</span>
                  <span style={{ fontSize: 15, color: "#6b7280" }}>/{interval === "yearly" ? t("perYear") : t("perMonth")}</span>
                </div>
                {interval === "yearly" && (
                  <div style={{ fontSize: 13, color: "#0A85CF", fontWeight: 500, marginBottom: 16 }}>{t("saveVsMonthly", { pct: plan.savings_yearly })}</div>
                )}
                {interval === "monthly" && <div style={{ marginBottom: 16 }} />}
                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 20px 0", flex: 1 }}>
                  {(plan.features || []).map((f, i) => (
                    <li key={i} style={{ fontSize: 14, color: "#374151", padding: "4px 0", display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ color: "#2e7d32", display: "inline-flex", flexShrink: 0 }}>{Ic.check}</span>
                      {subscriptionPlanFeatureLine(f, t)}
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <button
                    type="button"
                    className="ch-btn"
                    disabled
                    style={{ width: "100%", justifyContent: "center", gap: 8, opacity: 1, background: "#f0faf0", border: "1px solid #c8e6c9", color: "#2e7d32" }}
                  >
                    <span style={{ display: "inline-flex" }}>{Ic.check}</span>
                    {t("currentPlan")}
                  </button>
                ) : (
                  <button
                    className="ch-btn ch-btn-primary"
                    style={{
                      width: "100%",
                      justifyContent: "center",
                      background: plan.popular ? "#0A85CF" : "#1a1a1a",
                      borderColor: plan.popular ? "#0A85CF" : "#1a1a1a",
                    }}
                    onClick={() => startCheckout(priceId)}
                    disabled={checkoutLoading === priceId}
                  >
                    {checkoutLoading === priceId ? t("redirecting") : isActive ? t("changePlan") : t("startNow")}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div style={{ textAlign: "center", fontSize: 12, color: "#9ca3af", marginTop: 16 }}>{t("footerVat")}</div>
    </div>
  );
}
